import { useCallback } from 'react';
import type { CropArea } from './types';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useCropCanvas');

interface UseCropCanvasProps {
  maxLongSide?: number;
  /** JPEG output size budget in bytes. Defaults to 900KB. */
  maxFileSize?: number;
}

export interface CroppedImageResult {
  /**
   * Instant, synchronous data-URL of the cropped frame — usable as an
   * optimistic placeholder the moment the user confirms, before the (async)
   * JPEG size-budget compression below finishes. `null` when not requested.
   */
  previewUrl: string | null;
  /**
   * Background JPEG compression of the same frame to the size budget. Runs on
   * a detached canvas, so it keeps resolving even after the crop modal that
   * triggered it has unmounted.
   */
  blobPromise: Promise<Blob | null>;
}

interface UseCropCanvasReturn {
  cropImage: (
    imageElement: HTMLImageElement | null,
    cropArea: CropArea,
    options?: { emitPreview?: boolean }
  ) => CroppedImageResult;
}

/**
 * Crops + downscales an image to a detached canvas. The pixels are captured
 * synchronously (so the source object URL can be revoked / the modal can close
 * right away), while the final JPEG is compressed in the background.
 */
export function useCropCanvas({
  maxLongSide,
  maxFileSize = 900 * 1024,
}: UseCropCanvasProps): UseCropCanvasReturn {
  const cropImage = useCallback(
    (
      imageElement: HTMLImageElement | null,
      cropArea: CropArea,
      options?: { emitPreview?: boolean }
    ): CroppedImageResult => {
      const empty: CroppedImageResult = {
        previewUrl: null,
        blobPromise: Promise.resolve(null),
      };

      if (!imageElement) {
        console.error('Image not ready');
        return empty;
      }

      // Detached canvas (not mounted in the DOM) so compression survives the
      // crop modal closing immediately on confirm — same trick as the rotate
      // path. The drawImage below is synchronous, so the pixels are safely
      // captured before the caller revokes the source object URL.
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        return empty;
      }

      const pixelMultiple = 16;
      let targetWidth: number;
      let targetHeight: number;

      if (maxLongSide) {
        // Longest-side algorithm (for angles: 2048px), only downscale
        const longestSide = Math.max(cropArea.width, cropArea.height);
        if (longestSide <= maxLongSide) {
          targetWidth = Math.max(
            pixelMultiple,
            Math.round(cropArea.width / pixelMultiple) * pixelMultiple
          );
          targetHeight = Math.max(
            pixelMultiple,
            Math.round(cropArea.height / pixelMultiple) * pixelMultiple
          );
        } else {
          const scale = maxLongSide / longestSide;
          targetWidth = Math.max(
            pixelMultiple,
            Math.round((cropArea.width * scale) / pixelMultiple) * pixelMultiple
          );
          targetHeight = Math.max(
            pixelMultiple,
            Math.round((cropArea.height * scale) / pixelMultiple) * pixelMultiple
          );
        }
      } else {
        // Default: 1 megapixel target
        const aspectRatio = cropArea.width / cropArea.height;
        const targetPixels = 1000000;
        targetWidth =
          Math.round(Math.sqrt(targetPixels * aspectRatio) / pixelMultiple) *
          pixelMultiple;
        targetHeight =
          Math.round(targetWidth / aspectRatio / pixelMultiple) * pixelMultiple;
        const actualPixels = targetWidth * targetHeight;
        if (actualPixels > targetPixels) {
          const scale = Math.sqrt(targetPixels / actualPixels);
          targetWidth =
            Math.round((targetWidth * scale) / pixelMultiple) * pixelMultiple;
          targetHeight =
            Math.round((targetHeight * scale) / pixelMultiple) * pixelMultiple;
        }
      }

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw cropped and resized image (synchronous — pixels captured now)
      ctx.drawImage(
        imageElement,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        targetWidth,
        targetHeight
      );

      // Instant preview for an optimistic placeholder card — cheap because the
      // canvas is already at the (downscaled) target size.
      const previewUrl = options?.emitPreview
        ? canvas.toDataURL('image/jpeg', 0.7)
        : null;

      // Background compression to the size budget. The canvas already holds the
      // drawn pixels, so this needs neither the source image nor the DOM.
      const blobPromise = (async (): Promise<Blob | null> => {
        const tryQuality = (q: number): Promise<Blob | null> =>
          new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), 'image/jpeg', q);
          });

        let quality = 0.8;
        let blob: Blob | null = null;
        while (quality >= 0.3) {
          blob = await tryQuality(quality);
          if (blob && blob.size <= maxFileSize) {
            break;
          }
          quality -= 0.1;
        }

        if (blob) {
          logger.info('Cropped image created:', {
            targetDimensions: `${targetWidth}x${targetHeight}`,
            pixelCount: targetWidth * targetHeight,
            fileSize: blob.size,
            type: blob.type,
          });
        } else {
          console.error('Failed to create blob from canvas');
        }
        return blob;
      })();

      return { previewUrl, blobPromise };
    },
    [maxLongSide, maxFileSize]
  );

  return { cropImage };
}
